import React, { useState } from 'react';
import { Button, Modal } from '@themesberg/react-bootstrap';
import NewTable from '../../components/common/NewTable';
import { tripsViewColumns, vehiclePaymentExport } from './utils';
import { useDispatch, useSelector } from 'react-redux';
import { getTripInfoByObjectId } from '../../parse-functions/trips';
import { updateSpinnerState } from '../../actions/spinner';
import { updateToastInfo } from '../../actions/settings';
import TripSaveModal from './TripSaveModal';
import { useTranslation } from 'react-i18next';
import TripEditModal from './TripEditModal';
import ConfirmationModal from '../../components/common/confirmationModal';
import PerTripEditModal from './PerTripEditModal';
import { getPaymentsReport } from '../../parse-functions/reports';
import { exportMultiplePerDeliverySummaries } from '../../utils/excel';

const TripViewModal = ({
  show,
  handleClose,
  tripsData,
  handleSaveClick,
  extraData,
  formInput,
  type,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedTrip, setSelectedTrip] = useState();
  const [showEditModal, setShowEditModal] = useState(false);
  const [saveData, setSaveData] = useState({});
  const vendorsList = useSelector((state) => state?.vendors?.vendorsList || {});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  const isWHPaymentUser = useSelector(
    (state) => state?.user?.preferences?.isWHPaymentUser || false
  );
  const userName = useSelector((state) => state?.user?.user?.username || '');

  const [modalBody, setModalBody] = useState('');

  const handleSummaryExport = async () => {
    try {
      let reportData = await getPaymentsReport({
        startDate: new Date(extraData?.filter?.startDate),
        endDate: new Date(extraData?.filter?.endDate),
        vehicleNumber: extraData.vehicleDetails.vehicleNumber,
        filterType: 'Month',
        storeName: formInput.storeName,
        reportType: type,
        vendorObjectId: extraData.vehicleDetails?.vendorObjectId,
      });
      reportData = {
        ...reportData[0],
        totalAmount:
          (reportData[0]?.totalAmount || 0) +
          (reportData[0]?.voucherAmount || 0),
      };
      const data = [
        vehiclePaymentExport({
          extraData: reportData,
          userName,
        }),
      ];
      exportMultiplePerDeliverySummaries(
        data,
        `${extraData?.vehicleDetails?.vehicleNumber}_summary`
      );
    } catch (error) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Failed to fetch data!'),
          message: `${error.message}`,
        })
      );
    }
  };

  const handleEditClick = async (tripId, option, totalAmount) => {
    try {
      switch (option) {
        case 'Half Day':
          setShowConfirmModal(true);
          setModalBody(`Are you sure to mark it as half day? `);
          setSaveData({
            tripId,
            half: option,
            totalAmount: (totalAmount / 2).toFixed(2),
          });
          return;
        case 'Full Day':
          setShowConfirmModal(true);
          setModalBody('Are you sure to mark it as full day?');
          setSaveData({ tripId, half: option, totalAmount });
          return;
        case 'Leave':
          setShowConfirmModal(true);
          setModalBody('Are you sure to mark it as leave? New Amount:0');
          setSaveData({ tripId, half: option, totalAmount: 0 });
          return;
      }
      dispatch(updateSpinnerState(true));
      const tripDetails = await getTripInfoByObjectId(tripId, true);
      setSelectedTrip({
        trip: tripDetails.tripInfo,
        parcels: tripDetails.parcels,
      });
      setShowEditModal(true);
      dispatch(updateSpinnerState(false));
    } catch (error) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Failed to fetch data!'),
          message: `${error.message}`,
        })
      );
    }
  };

  const handleSubmitClick = (data) => {
    setSaveData(data);
    setShowSaveModal(true);
  };

  const onTripViewClick = (tripId) => {
    window.open(`${window.location.origin}/tripdetails/${tripId}`, '_blank');
  };

  return (
    <>
      <Modal show={show} centered onHide={handleClose} fullscreen={true}>
        <Modal.Header>
          <Modal.Title>
            <h5>Total Trips: {tripsData?.length}</h5>
          </Modal.Title>
          <div className="me-4 d-flex gap-3 align-items-center">
            {extraData.filter.isMonthExport && (
              <Button variant="primary" onClick={handleSummaryExport}>
                Export Summary
              </Button>
            )}
            <Button variant="close" aria-label="Close" onClick={handleClose} />
          </div>
        </Modal.Header>
        <Modal.Body>
          <NewTable
            id={'viewTrips'}
            data={tripsData}
            columns={tripsViewColumns({
              handleEditClick,
              handleSaveClick,
              onTripViewClick,
              isAdmin: isAdmin || isWHPaymentUser,
            })}
          />
        </Modal.Body>
      </Modal>
      {[selectedTrip && 'fixedPerDelivery', 'extraPerDelivery'].includes(
        selectedTrip?.trip?.driverInfo?.paymentMode
      ) ? (
        <TripEditModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          tripDetails={selectedTrip}
          onSubmit={handleSubmitClick}
          handleSaveClick={handleSaveClick}
        />
      ) : (
        <PerTripEditModal
          onSubmit={handleSaveClick}
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          tripData={selectedTrip?.trip}
          vendorsList={vendorsList}
        />
      )}
      {Object.keys(saveData).length > 0 && (
        <TripSaveModal
          show={showSaveModal}
          handleClose={() => setShowSaveModal(false)}
          saveData={saveData}
          setSaveData={setSaveData}
          tripDetails={selectedTrip}
          onSubmit={handleSaveClick}
          vendorsList={vendorsList}
          setShowEditModal={setShowEditModal}
        />
      )}
      {
        <ConfirmationModal
          showModal={showConfirmModal}
          closeModal={() => setShowConfirmModal(false)}
          modalBody={modalBody}
          modalTitle={'Edit Trip'}
          onConfirm={async () => {
            await handleSaveClick(saveData);
            setShowConfirmModal(false);
          }}
        />
      }
    </>
  );
};

export default TripViewModal;
